import type { PostgrestError } from '@supabase/supabase-js';
import { resolveRatePaise } from '@/lib/pricing';
import { normalizeCardUid } from '@/lib/cards';
import { clubDateISO } from '@/lib/dates';
import { AppError } from '@/lib/errors';
import { uid as genId } from '@/lib/id';
import type {
  ActivityEvent,
  AppUser,
  AttendanceRecord,
  Booking,
  CardRole,
  CardTap,
  Club,
  Customer,
  Expense,
  FoodOrder,
  LightState,
  Member,
  MembershipPlan,
  MenuItem,
  Payment,
  PiStatus,
  RateWindow,
  ReaderStatus,
  Receptionist,
  Session,
  Station,
  StationType,
  WalletTransaction,
} from '@/types/domain';
import type { DataProvider, FoodOrderFilter, RateBandInput, WalletMutationResult } from '../client';
import { supabase } from './client';
import {
  SUPA_CLUB_ID,
  bookingStatusOut,
  expandRateWindow,
  groupSessionOrders,
  mapAttendance,
  mapBooking,
  mapCardTap,
  mapMember,
  mapMenuItem,
  mapPlan,
  mapSession,
  mapStation,
  mapUser,
  mapWalletTxn,
  methodOut,
  paiseToRupees,
  sessionToPayment,
  synthClub,
} from './mappers';

function unwrap<T>(res: { data: T; error: PostgrestError | null }): T {
  if (res.error) throw new AppError(res.error.message, 'UNKNOWN');
  return res.data;
}

/**
 * Adapts the app's data seam to the EXISTING Break Point Club database
 * (tables/profiles/sessions/…). Converts rupees↔paise, derives station status
 * from live sessions, expands rate windows, groups session_orders, and layers
 * the new membership/wallet/card tables on top.
 */
export class SupabaseProvider implements DataProvider {
  private cachedUser: { id: string; name: string } | null = null;

  private sb() {
    return supabase();
  }

  private async userId(): Promise<string | null> {
    if (this.cachedUser) return this.cachedUser.id;
    const { data } = await this.sb().auth.getUser();
    return data.user?.id ?? null;
  }

  private async rawRateWindows(): Promise<RateWindow[]> {
    const rows = unwrap(await this.sb().from('rate_windows').select('*'));
    return (rows ?? []).flatMap(expandRateWindow);
  }

  private async liveSessionsByTable(): Promise<Map<string, Session>> {
    // The public (anon) site can read station names but not sessions (PII). If
    // the read is refused, treat it as "nothing live" so listStations still
    // works for the customer booking page instead of failing outright.
    const res = await this.sb().from('sessions').select('*').in('status', ['ACTIVE', 'PAUSED']);
    if (res.error) return new Map();
    const rows = res.data;
    const windows = (rows ?? []).length ? await this.rawRateWindows() : [];
    const map = new Map<string, Session>();
    for (const r of rows ?? []) {
      const rate = r.rate_per_minute != null ? Math.round(Number(r.rate_per_minute) * 100) : 0;
      map.set(r.table_id, mapSession(r, rate || this.rateForRow(r, windows)));
    }
    return map;
  }

  private rateForRow(row: Record<string, any>, windows: RateWindow[]): number {
    // Resolve a per-minute rate for a session lacking an explicit rate snapshot.
    const tableType = (row.table_type as StationType) ?? 'POOL';
    return resolveRatePaise(windows, tableType, row.started_at);
  }

  // ---- clubs ----------------------------------------------------------------
  async listClubs(): Promise<Club[]> {
    return [synthClub()];
  }
  async getClub(_id: string): Promise<Club | null> {
    return synthClub();
  }

  // ---- users ----------------------------------------------------------------
  async listUsers(_clubId: string): Promise<AppUser[]> {
    const rows = unwrap(await this.sb().from('profiles').select('*').order('created_at'));
    return (rows ?? []).map(mapUser);
  }
  async findUserByEmail(email: string): Promise<AppUser | null> {
    const rows = unwrap(
      await this.sb().from('profiles').select('*').ilike('email', email).limit(1),
    );
    return rows && rows[0] ? mapUser(rows[0]) : null;
  }
  async updateUser(id: string, patch: Partial<AppUser>): Promise<AppUser> {
    const update: Record<string, unknown> = {};
    if (patch.status) update.active = patch.status === 'ACTIVE';
    if (patch.role) update.role = patch.role === 'MASTER_ADMIN' || patch.role === 'ADMIN' ? 'admin' : patch.role === 'MANAGER' ? 'billing' : 'food';
    const row = unwrap(
      await this.sb().from('profiles').update(update).eq('id', id).select().single(),
    );
    return mapUser(row);
  }

  // ---- stations -------------------------------------------------------------
  async listStations(_clubId: string): Promise<Station[]> {
    const [rows, live] = await Promise.all([
      this.sb().from('tables').select('*').then(unwrap),
      this.liveSessionsByTable(),
    ]);
    return (rows ?? [])
      .map((r: Record<string, any>) => mapStation(r, live.get(r.id) ?? null))
      .sort((a: Station, b: Station) => a.sortOrder - b.sortOrder);
  }
  async getStation(id: string): Promise<Station | null> {
    const rows = unwrap(await this.sb().from('tables').select('*').eq('id', id).limit(1));
    if (!rows || !rows[0]) return null;
    const live = await this.liveSessionsByTable();
    return mapStation(rows[0], live.get(id) ?? null);
  }
  async updateStation(id: string, patch: Partial<Station>): Promise<Station> {
    // `tables` has no status column — station status is derived from sessions, so
    // status-only updates (start/stop) are a no-op here. Only real columns write.
    const update: Record<string, unknown> = {};
    if (patch.name !== undefined) update.name = patch.name;
    if (patch.gpioPin !== undefined) update.gpio_pin = patch.gpioPin;
    if (Object.keys(update).length > 0) {
      unwrap(await this.sb().from('tables').update(update).eq('id', id).select().single());
    }
    const station = await this.getStation(id);
    if (!station) throw new AppError('Station not found', 'NOT_FOUND');
    return station;
  }

  // ---- rate windows ---------------------------------------------------------
  async listRateWindows(_clubId: string): Promise<RateWindow[]> {
    return this.rawRateWindows();
  }
  async createRateWindow(): Promise<RateWindow> {
    throw new AppError('Use the Add window action.', 'VALIDATION');
  }
  async createRateWindowBand(input: RateBandInput): Promise<void> {
    // One rate_windows row carries the time range + all three per-type rates.
    // A full-day band is start === end in app terms, but the live table's
    // rate_windows_end_min_check rejects end_min = 0, so store it as 0..1440
    // (midnight-to-midnight) — expandRateWindow reads that back as all-day.
    const fullDay = input.startMinute === input.endMinute;
    unwrap(
      await this.sb()
        .from('rate_windows')
        .insert({
          label: input.label,
          start_min: fullDay ? 0 : input.startMinute,
          end_min: fullDay ? 1440 : input.endMinute,
          rate_pool: paiseToRupees(input.rates.POOL ?? 0),
          rate_mini_snooker: paiseToRupees(input.rates.MINI_SNOOKER ?? 0),
          rate_snooker: paiseToRupees(input.rates.SNOOKER ?? 0),
        })
        .select(),
    );
  }
  async updateRateWindow(id: string, patch: Partial<RateWindow>): Promise<RateWindow> {
    // App-level window ids are `${rowId}:${TYPE}` (see expandRateWindow); a single
    // rate_windows row carries all three per-type rates + the time range. Map the
    // type back to its column so an admin editing one amount/time writes the row.
    const sep = id.lastIndexOf(':');
    const rowId = sep >= 0 ? id.slice(0, sep) : id;
    const type = (sep >= 0 ? id.slice(sep + 1) : '') as StationType;
    const COLUMN: Record<StationType, string> = {
      POOL: 'rate_pool',
      MINI_SNOOKER: 'rate_mini_snooker',
      SNOOKER: 'rate_snooker',
    };
    const update: Record<string, unknown> = {};
    if (patch.ratePerMinutePaise !== undefined && COLUMN[type]) {
      update[COLUMN[type]] = paiseToRupees(patch.ratePerMinutePaise);
    }
    if (patch.label !== undefined) update.label = patch.label;
    if (patch.startMinute !== undefined) update.start_min = patch.startMinute;
    if (patch.endMinute !== undefined) update.end_min = patch.endMinute;

    const query =
      Object.keys(update).length > 0
        ? this.sb().from('rate_windows').update(update).eq('id', rowId).select('*')
        : this.sb().from('rate_windows').select('*').eq('id', rowId);
    const rows = unwrap(await query);
    const row = rows && rows[0];
    if (!row) throw new AppError('Rate window not found', 'NOT_FOUND');
    const expanded = expandRateWindow(row);
    return expanded.find((w) => w.stationType === type) ?? expanded[0]!;
  }
  async deleteRateWindow(id: string): Promise<void> {
    // Delete the whole row for this band (idempotent — all three per-type ids
    // map to the same rowId, so repeated calls just affect zero rows).
    const sep = id.lastIndexOf(':');
    const rowId = sep >= 0 ? id.slice(0, sep) : id;
    unwrap(await this.sb().from('rate_windows').delete().eq('id', rowId).select());
  }

  // ---- sessions -------------------------------------------------------------
  async listSessions(_clubId: string): Promise<Session[]> {
    const [rows, windows] = await Promise.all([
      this.sb().from('sessions').select('*').order('started_at', { ascending: false }).limit(500).then(unwrap),
      this.rawRateWindows(),
    ]);
    return (rows ?? []).map((r: Record<string, any>) => {
      const rate = r.rate_per_minute != null ? Math.round(Number(r.rate_per_minute) * 100) : this.rateForRow(r, windows);
      return mapSession(r, rate);
    });
  }
  async getSession(id: string): Promise<Session | null> {
    const rows = unwrap(await this.sb().from('sessions').select('*').eq('id', id).limit(1));
    if (!rows || !rows[0]) return null;
    const r = rows[0];
    const rate = r.rate_per_minute != null ? Math.round(Number(r.rate_per_minute) * 100) : 0;
    return mapSession(r, rate);
  }
  async createSession(input: Omit<Session, 'id'>): Promise<Session> {
    const row = unwrap(
      await this.sb()
        .from('sessions')
        .insert({
          table_id: input.stationId,
          status: 'ACTIVE',
          started_at: input.startedAt,
          rate_per_minute: paiseToRupees(input.ratePerMinutePaise),
          customer_name: input.customerName,
          player_count: input.playerCount,
          opened_by: await this.userId(),
          last_active_at: input.startedAt,
        })
        .select()
        .single(),
    );
    return mapSession(row, input.ratePerMinutePaise);
  }
  async updateSession(id: string, patch: Partial<Session>): Promise<Session> {
    const update: Record<string, unknown> = {};
    if (patch.status !== undefined) update.status = patch.status;
    if (patch.endedAt !== undefined) update.ended_at = patch.endedAt;
    if (patch.pausedAt !== undefined) update.paused_at = patch.pausedAt;
    if (patch.pausedMs !== undefined) update.paused_seconds = Math.round(patch.pausedMs / 1000);
    update.last_active_at = new Date().toISOString();

    // On completion, freeze active_seconds for the monthly_summary view.
    if (patch.status === 'COMPLETED' && patch.endedAt) {
      const current = await this.getSession(id);
      if (current) {
        const grossSec = Math.max(0, (new Date(patch.endedAt).getTime() - new Date(current.startedAt).getTime()) / 1000);
        const pausedSec = Math.round((patch.pausedMs ?? current.pausedMs) / 1000);
        update.active_seconds = Math.max(0, Math.round(grossSec - pausedSec));
      }
    }

    const row = unwrap(await this.sb().from('sessions').update(update).eq('id', id).select().single());
    const rate = row.rate_per_minute != null ? Math.round(Number(row.rate_per_minute) * 100) : 0;
    return mapSession(row, rate);
  }

  // ---- menu -----------------------------------------------------------------
  async listMenuItems(_clubId: string): Promise<MenuItem[]> {
    const rows = unwrap(await this.sb().from('menu_items').select('*').order('name'));
    return (rows ?? []).map(mapMenuItem);
  }
  async createMenuItem(input: Omit<MenuItem, 'id'>): Promise<MenuItem> {
    const row = unwrap(
      await this.sb()
        .from('menu_items')
        .insert({
          name: input.name,
          kind: input.category === 'BAR' ? 'cigarette' : 'food',
          category: input.category === 'BEVERAGE' ? 'Drinks' : null,
          price: paiseToRupees(input.pricePaise),
          active: input.available,
        })
        .select()
        .single(),
    );
    return mapMenuItem(row);
  }
  async updateMenuItem(id: string, patch: Partial<MenuItem>): Promise<MenuItem> {
    const update: Record<string, unknown> = {};
    if (patch.name !== undefined) update.name = patch.name;
    if (patch.pricePaise !== undefined) update.price = paiseToRupees(patch.pricePaise);
    if (patch.available !== undefined) update.active = patch.available;
    const row = unwrap(await this.sb().from('menu_items').update(update).eq('id', id).select().single());
    return mapMenuItem(row);
  }

  // ---- food orders ----------------------------------------------------------
  async listFoodOrders(_clubId: string, filter?: FoodOrderFilter) {
    if (filter && 'sessionId' in filter) {
      if (!filter.sessionId) return [];
      const [rows, session] = await Promise.all([
        this.sb().from('session_orders').select('*').eq('session_id', filter.sessionId).then(unwrap),
        this.getSession(filter.sessionId),
      ]);
      if (!rows || rows.length === 0) return [];
      return [groupSessionOrders(rows, filter.sessionId, session?.status !== 'COMPLETED')];
    }
    const rows = unwrap(
      await this.sb().from('session_orders').select('*').order('created_at', { ascending: false }).limit(500),
    );
    const bySession = new Map<string, Record<string, any>[]>();
    for (const r of rows ?? []) {
      const arr = bySession.get(r.session_id) ?? [];
      arr.push(r);
      bySession.set(r.session_id, arr);
    }
    return [...bySession.entries()].map(([sid, rs]) => groupSessionOrders(rs, sid, false));
  }
  async createFoodOrder(input: Omit<FoodOrder, 'id'>) {
    const session = input.sessionId ? await this.getSession(input.sessionId) : null;
    const tableId = session?.stationId;
    const userId = await this.userId();
    const payload = input.lines.map((l) => ({
      session_id: input.sessionId,
      table_id: tableId,
      item_id: l.menuItemId,
      item_name: l.name,
      qty: l.quantity,
      unit_price: paiseToRupees(l.unitPricePaise),
      line_total: paiseToRupees(l.unitPricePaise * l.quantity),
      added_by: userId,
    }));
    const rows = unwrap(await this.sb().from('session_orders').insert(payload).select());
    return groupSessionOrders(rows ?? [], input.sessionId ?? 'counter', true);
  }
  async updateFoodOrder(id: string, patch: Partial<FoodOrder>): Promise<FoodOrder> {
    // session_orders are line rows with no status; completion happens at settle.
    return {
      id,
      clubId: SUPA_CLUB_ID,
      sessionId: null,
      lines: [],
      status: patch.status ?? 'COMPLETED',
      createdAt: new Date().toISOString(),
    };
  }

  // ---- bookings -------------------------------------------------------------
  async listBookings(_clubId: string): Promise<Booking[]> {
    const [rows, tables] = await Promise.all([
      this.sb().from('bookings').select('*').order('starts_at', { ascending: false }).limit(200).then(unwrap),
      this.sb().from('tables').select('id,type').then(unwrap),
    ]);
    const typeById = new Map<string, StationType>((tables ?? []).map((t: Record<string, any>) => [t.id, t.type]));
    return (rows ?? []).map((r: Record<string, any>) => mapBooking(r, typeById.get(r.table_id) ?? null));
  }
  async createBooking(input: Omit<Booking, 'id'>): Promise<Booking> {
    const endsAt = new Date(new Date(input.startAt).getTime() + input.durationMinutes * 60000).toISOString();
    const row = unwrap(
      await this.sb()
        .from('bookings')
        .insert({
          table_id: input.stationId,
          customer_name: input.customerName,
          phone: input.phone,
          starts_at: input.startAt,
          ends_at: endsAt,
          status: bookingStatusOut(input.status),
          created_by: await this.userId(),
        })
        .select()
        .single(),
    );
    return mapBooking(row, input.stationType);
  }
  async createBookingPublic(input: Omit<Booking, 'id'>): Promise<Booking> {
    // Public (anon) booking — no payment gateway. Insert WITHOUT a read-back:
    // anon may INSERT a booking but must never SELECT the bookings book (PII),
    // so a `.select()` here would be refused. The row's real id stays server-side
    // (the UI only needs a confirmation object; the list refetches from the DB).
    const endsAt = new Date(
      new Date(input.startAt).getTime() + input.durationMinutes * 60000,
    ).toISOString();
    const { error } = await this.sb()
      .from('bookings')
      .insert({
        table_id: input.stationId,
        customer_name: input.customerName,
        phone: input.phone,
        starts_at: input.startAt,
        ends_at: endsAt,
        status: bookingStatusOut(input.status),
      });
    if (error) {
      // The bookings_no_overlap exclusion constraint rejects a clashing slot.
      if (error.code === '23P01' || /overlap|exclusion|no_overlap/i.test(error.message ?? '')) {
        throw new AppError('That slot was just taken — please pick another.', 'CONFLICT');
      }
      throw new AppError(error.message || 'Could not book — please try again.', 'UNKNOWN');
    }
    return { ...input, id: `bk-${crypto.randomUUID()}` };
  }
  async updateBooking(id: string, patch: Partial<Booking>): Promise<Booking> {
    const update: Record<string, unknown> = {};
    if (patch.status !== undefined) update.status = bookingStatusOut(patch.status);
    const row = unwrap(await this.sb().from('bookings').update(update).eq('id', id).select().single());
    return mapBooking(row, patch.stationType ?? null);
  }

  // ---- customers & payments -------------------------------------------------
  async listCustomers(_clubId: string): Promise<Customer[]> {
    return []; // no customers table in the existing schema yet
  }
  async listPayments(_clubId: string): Promise<Payment[]> {
    const rows = unwrap(
      await this.sb()
        .from('sessions')
        .select('*')
        .eq('status', 'COMPLETED')
        .not('payment_method', 'is', null)
        .order('ended_at', { ascending: false })
        .limit(500),
    );
    return (rows ?? []).map(sessionToPayment);
  }
  async createPayment(input: Omit<Payment, 'id'>): Promise<Payment> {
    // The old app records payment on the session row itself.
    if (input.sessionId) {
      const orders = unwrap(
        await this.sb().from('session_orders').select('unit_price,qty').eq('session_id', input.sessionId),
      );
      const foodRupees = (orders ?? []).reduce((s: number, o: Record<string, any>) => s + Number(o.unit_price) * o.qty, 0);
      const totalRupees = paiseToRupees(input.amountPaise);
      const isComp = input.method === 'COMP';
      unwrap(
        await this.sb()
          .from('sessions')
          .update({
            payment_method: methodOut(input.method),
            total_amount: isComp ? 0 : totalRupees,
            food_amount: isComp ? 0 : foodRupees,
            table_amount: isComp ? 0 : Math.max(0, totalRupees - foodRupees),
            member_id: input.memberId ?? null,
            closed_by: await this.userId(),
            closed_by_name: this.cachedUser?.name ?? null,
            billing_reason: isComp ? 'Cleared by admin' : `Settled · ${input.method}`,
          })
          .eq('id', input.sessionId)
          .select()
          .single(),
      );
    }
    return { ...input, id: genId('pay') };
  }

  // ---- expenses -------------------------------------------------------------
  async listExpenses(_clubId: string): Promise<Expense[]> {
    const res = await this.sb().from('expenses').select('*').order('incurred_at', { ascending: false }).limit(200);
    if (res.error) return []; // table may not exist yet
    return (res.data ?? []).map((r: Record<string, any>) => ({
      id: r.id,
      clubId: SUPA_CLUB_ID,
      category: r.category,
      amountPaise: Math.round(Number(r.amount) * 100),
      note: r.note ?? null,
      incurredAt: r.incurred_at,
    }));
  }
  async createExpense(input: Omit<Expense, 'id'>): Promise<Expense> {
    const row = unwrap(
      await this.sb()
        .from('expenses')
        .insert({ category: input.category, amount: paiseToRupees(input.amountPaise), note: input.note, incurred_at: input.incurredAt })
        .select()
        .single(),
    );
    return { id: row.id, clubId: SUPA_CLUB_ID, category: row.category, amountPaise: Math.round(Number(row.amount) * 100), note: row.note ?? null, incurredAt: row.incurred_at };
  }

  // ---- activity -------------------------------------------------------------
  async listActivity(_clubId: string, limit = 8): Promise<ActivityEvent[]> {
    const rows = unwrap(
      await this.sb()
        .from('sessions')
        .select('id,table_id,total_amount,payment_method,ended_at')
        .eq('status', 'COMPLETED')
        .order('ended_at', { ascending: false })
        .limit(limit),
    );
    return (rows ?? []).map((r: Record<string, any>) => ({
      id: `${r.id}:act`,
      clubId: SUPA_CLUB_ID,
      type: 'SESSION_END' as const,
      message: `${r.table_id} session settled`,
      amountPaise: Math.round(Number(r.total_amount || 0) * 100),
      at: r.ended_at,
    }));
  }
  async addActivity(input: Omit<ActivityEvent, 'id'>): Promise<ActivityEvent> {
    // No activity table yet — activity is derived from sessions. No-op write.
    return { ...input, id: genId('act') };
  }

  // ---- auth -----------------------------------------------------------------
  async signIn(login: string, password: string): Promise<AppUser> {
    const sb = this.sb();
    let email = login.trim();
    const { data: resolved } = await sb.rpc('email_for_login', { p_login: login.trim() });
    if (typeof resolved === 'string' && resolved.length > 0) email = resolved;

    const { data, error } = await sb.auth.signInWithPassword({ email, password });
    if (error) throw new AppError(error.message, 'FORBIDDEN');
    const authId = data.user?.id;
    if (!authId) throw new AppError('Sign in failed', 'UNKNOWN');

    const rows = unwrap(await sb.from('profiles').select('*').eq('id', authId).limit(1));
    const profile = rows && rows[0];
    if (!profile) throw new AppError('No operator profile found for this account.', 'NOT_FOUND');
    if (!profile.active) {
      await sb.auth.signOut();
      throw new AppError('Your account is awaiting admin approval.', 'FORBIDDEN');
    }
    const user = mapUser(profile);
    this.cachedUser = { id: user.id, name: user.name };
    return user;
  }
  async signOut(): Promise<void> {
    this.cachedUser = null;
    await this.sb().auth.signOut();
  }
  async signUp(input: { name: string; email: string; password: string }): Promise<void> {
    const sb = this.sb();
    const { error } = await sb.auth.signUp({
      email: input.email.trim(),
      password: input.password,
      options: { data: { display_name: input.name.trim() } },
    });
    if (error) throw new AppError(error.message, 'VALIDATION');
    // A DB trigger creates the pending profile (role 'food', inactive). If the
    // project has email confirmation off, sign-up also opens a session — drop it
    // so a not-yet-approved account can't act before an admin approves it.
    this.cachedUser = null;
    await sb.auth.signOut();
  }
  async requestPasswordReset(login: string): Promise<void> {
    const sb = this.sb();
    let email = login.trim();
    const { data: resolved } = await sb.rpc('email_for_login', { p_login: login.trim() });
    if (typeof resolved === 'string' && resolved.length > 0) email = resolved;
    const redirectTo = `${window.location.origin}/reset-password`;
    const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo });
    if (error) throw new AppError(error.message, 'VALIDATION');
  }
  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await this.sb().auth.updateUser({ password: newPassword });
    if (error) throw new AppError(error.message, 'VALIDATION');
  }
  onAuthRecovery(ready: () => void): () => void {
    const sb = this.sb();
    // Creating the client processes the recovery token in the URL hash; if a
    // session is already present fire now, otherwise wait for the auth event.
    void sb.auth.getSession().then(({ data }) => {
      if (data.session) ready();
    });
    const { data } = sb.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) ready();
    });
    return () => data.subscription.unsubscribe();
  }

  // ---- members & wallet -----------------------------------------------------
  async listMembers(_clubId: string): Promise<Member[]> {
    const rows = unwrap(await this.sb().from('members').select('*, member_cards(*)').order('name'));
    return (rows ?? []).map(mapMember);
  }
  async getMember(id: string): Promise<Member | null> {
    const rows = unwrap(await this.sb().from('members').select('*, member_cards(*)').eq('id', id).limit(1));
    return rows && rows[0] ? mapMember(rows[0]) : null;
  }
  async findMemberByCardUid(_clubId: string, cardUid: string): Promise<Member | null> {
    const target = normalizeCardUid(cardUid);
    const rows = unwrap(
      await this.sb().from('member_cards').select('members(*, member_cards(*))').ilike('uid', target).limit(1),
    );
    const m = rows && rows[0] && (rows[0] as Record<string, any>).members;
    return m ? mapMember(m) : null;
  }
  async createMember(input: Omit<Member, 'id'>): Promise<Member> {
    const row = unwrap(
      await this.sb().from('members').insert({ name: input.name, phone: input.phone }).select('*, member_cards(*)').single(),
    );
    return mapMember(row);
  }
  async updateMember(id: string, patch: Partial<Member>): Promise<Member> {
    const update: Record<string, unknown> = {};
    if (patch.name !== undefined) update.name = patch.name;
    if (patch.phone !== undefined) update.phone = patch.phone;
    if (patch.status !== undefined) update.status = patch.status;
    unwrap(await this.sb().from('members').update(update).eq('id', id).select().single());
    return (await this.getMember(id))!;
  }
  async linkCard(memberId: string, cardUid: string, label = 'Card', role: CardRole = 'MEMBER'): Promise<Member> {
    const target = normalizeCardUid(cardUid);
    const res = await this.sb().from('member_cards').insert({ member_id: memberId, uid: target, label, role }).select();
    if (res.error) {
      if (res.error.code === '23505') throw new AppError('Tag already linked to a member', 'CONFLICT');
      throw new AppError(res.error.message, 'UNKNOWN');
    }
    return (await this.getMember(memberId))!;
  }
  async unlinkCard(memberId: string, cardUid: string): Promise<Member> {
    unwrap(await this.sb().from('member_cards').delete().eq('member_id', memberId).ilike('uid', normalizeCardUid(cardUid)).select());
    return (await this.getMember(memberId))!;
  }
  async topUpWallet(memberId: string, amountPaise: number, note: string): Promise<WalletMutationResult> {
    const txnRow = unwrap(
      await this.sb().rpc('topup_wallet', { p_member_id: memberId, p_amount: paiseToRupees(amountPaise), p_note: note }),
    );
    const member = await this.getMember(memberId);
    return { member: member!, transaction: mapWalletTxn(txnRow) };
  }
  async debitWallet(
    memberId: string,
    amountPaise: number,
    note: string,
    ref?: { sessionId?: string | null; paymentId?: string | null },
  ): Promise<WalletMutationResult> {
    const txnRow = unwrap(
      await this.sb().rpc('debit_wallet', {
        p_member_id: memberId,
        p_amount: paiseToRupees(amountPaise),
        p_note: note,
        p_session_id: ref?.sessionId ?? null,
      }),
    );
    const member = await this.getMember(memberId);
    return { member: member!, transaction: mapWalletTxn(txnRow) };
  }
  async listWalletTransactions(_clubId: string, memberId?: string): Promise<WalletTransaction[]> {
    let q = this.sb().from('wallet_transactions').select('*').order('created_at', { ascending: false }).limit(100);
    if (memberId) q = q.eq('member_id', memberId);
    const rows = unwrap(await q);
    return (rows ?? []).map(mapWalletTxn);
  }

  // ---- membership plans -----------------------------------------------------
  async listMembershipPlans(_clubId: string): Promise<MembershipPlan[]> {
    const rows = unwrap(await this.sb().from('membership_plans').select('*'));
    return (rows ?? []).map(mapPlan);
  }

  // ---- attendance -----------------------------------------------------------
  async listReceptionists(_clubId: string): Promise<Receptionist[]> {
    const res = await this.sb().from('receptionists').select('*').eq('active', true).order('name');
    if (res.error) return []; // table may not exist yet
    return (res.data ?? []).map((r: Record<string, any>) => ({
      id: r.id,
      clubId: SUPA_CLUB_ID,
      name: r.name,
      active: r.active !== false,
      createdAt: r.created_at,
    }));
  }
  async addReceptionist(_clubId: string, name: string): Promise<Receptionist> {
    const row = unwrap(
      await this.sb().from('receptionists').insert({ name: name.trim(), active: true }).select().single(),
    );
    return { id: row.id, clubId: SUPA_CLUB_ID, name: row.name, active: row.active !== false, createdAt: row.created_at };
  }
  async removeReceptionist(id: string): Promise<void> {
    unwrap(await this.sb().from('receptionists').update({ active: false }).eq('id', id).select());
  }
  async listAttendance(_clubId: string): Promise<AttendanceRecord[]> {
    const res = await this.sb()
      .from('attendance')
      .select('*')
      .order('clock_in_at', { ascending: false })
      .limit(300);
    if (res.error) return [];
    return (res.data ?? []).map(mapAttendance);
  }
  async clockIn(_clubId: string, receptionistId: string): Promise<AttendanceRecord> {
    const person = unwrap(
      await this.sb().from('receptionists').select('name').eq('id', receptionistId).single(),
    );
    if (!person) throw new AppError('Receptionist not found', 'NOT_FOUND');
    const open = unwrap(
      await this.sb()
        .from('attendance')
        .select('id')
        .eq('receptionist_id', receptionistId)
        .is('clock_out_at', null)
        .limit(1),
    );
    if (open && open.length > 0) {
      throw new AppError(`${person.name} is already clocked in`, 'CONFLICT');
    }
    const now = new Date().toISOString();
    const row = unwrap(
      await this.sb()
        .from('attendance')
        .insert({
          receptionist_id: receptionistId,
          receptionist_name: person.name,
          work_date: clubDateISO(new Date(now)),
          clock_in_at: now,
        })
        .select()
        .single(),
    );
    return mapAttendance(row);
  }
  async clockOut(recordId: string): Promise<AttendanceRecord> {
    const rows = unwrap(
      await this.sb()
        .from('attendance')
        .update({ clock_out_at: new Date().toISOString() })
        .eq('id', recordId)
        .is('clock_out_at', null)
        .select(),
    );
    if (!rows || rows.length === 0) throw new AppError('This shift is already closed', 'CONFLICT');
    return mapAttendance(rows[0]);
  }

  // ---- reader ---------------------------------------------------------------
  async getReaderStatus(): Promise<ReaderStatus> {
    return { online: true, mode: 'live', readerId: 'reader-1', lastTapAt: null };
  }
  async waitForCardTap(timeoutMs: number, signal?: AbortSignal): Promise<CardTap> {
    const sb = this.sb();
    return new Promise<CardTap>((resolve, reject) => {
      const channel = sb
        .channel(`card_taps_${genId()}`)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'card_taps' }, (payload) => {
          cleanup();
          resolve(mapCardTap(payload.new as Record<string, any>));
        })
        .subscribe();
      const timer = setTimeout(() => {
        cleanup();
        reject(new AppError('No card detected — tap again.', 'NOT_FOUND'));
      }, timeoutMs);
      const onAbort = () => {
        cleanup();
        reject(new AppError('Cancelled', 'CONFLICT'));
      };
      signal?.addEventListener('abort', onAbort);
      function cleanup() {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
        void sb.removeChannel(channel);
      }
    });
  }
  async simulateCardTap(_cardUid: string): Promise<CardTap> {
    throw new AppError('Simulated taps are off on the live backend — use a real card.', 'VALIDATION');
  }

  // ---- pi lighting (no Pi backend yet — derived/no-op) ----------------------
  async getPiStatus(): Promise<PiStatus> {
    return { online: false, mode: 'live', lastSeen: null, lights: [] };
  }
  async setLight(stationId: string, on: boolean): Promise<LightState> {
    return { stationId, gpioPin: null, on, updatedAt: new Date().toISOString() };
  }
}
